/**
 * FeedbackWidget.jsx
 *
 * Floating bottom-left pill that:
 *   (a) is ALWAYS visible while signed in (manual trigger),
 *   (b) auto-prompts ONCE after a small dwell time, with a card that lifts
 *       in from the bottom and asks "How's QuotesFlow so far?".
 *
 * Lightweight form: 1-5 star rating (required) + free-text message.
 * The "comprehensive" version lives at /app/feedback as a dedicated page.
 *
 * The auto-prompt is per-user and stored in localStorage; we also defer it
 * for a short period after dismissal so it never feels naggy.
 *
 * Hidden on /login, /signup and the public quote viewer (/q/*) — and on the
 * /app/feedback page itself, where the full form is already on-screen.
 *
 * Uses bottom-LEFT positioning so it never overlaps the SupportChat pill in
 * bottom-right.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquareHeart, X, Star, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth.jsx';
import api from '@/lib/api';

const AUTO_PROMPT_DELAY_MS = 1000 * 60 * 2;          // 2 min after page load (snappy demo-friendly)
const AUTO_PROMPT_KEY = 'qf-feedback-auto-prompted'; // per-user marker so we never auto-prompt twice
const DISMISS_TIMESTAMP_KEY = 'qf-feedback-dismissed-at'; // soft cooldown

function _markerKey(user) {
    return user?.id ? `${AUTO_PROMPT_KEY}:${user.id}` : null;
}

export function FeedbackWidget() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const textareaRef = useRef(null);

    // Hide on auth, public viewer, and on the full-page feedback form.
    const path = location.pathname || '';
    const hidden =
        path === '/login' ||
        path === '/signup' ||
        path.startsWith('/q/') ||
        path === '/app/feedback';

    // Auto-prompt logic — once per user, with a soft dismiss cooldown.
    useEffect(() => {
        if (!user || hidden) return;
        const key = _markerKey(user);
        if (!key) return;
        if (localStorage.getItem(key)) return; // already prompted this user
        // Respect a recent dismissal across pageloads.
        const dismissedAt = parseInt(localStorage.getItem(DISMISS_TIMESTAMP_KEY) || '0', 10);
        const sinceDismiss = Date.now() - dismissedAt;
        if (dismissedAt && sinceDismiss < 1000 * 60 * 60 * 24) return; // 24h cooldown after a dismiss
        const t = setTimeout(() => {
            setOpen(true);
            localStorage.setItem(key, '1');
        }, AUTO_PROMPT_DELAY_MS);
        return () => clearTimeout(t);
    }, [user?.id, hidden]);

    useEffect(() => {
        if (open && textareaRef.current) {
            // Don't autofocus aggressively on mobile; only on desktop wide screens.
            if (window.matchMedia('(min-width: 768px)').matches) {
                setTimeout(() => textareaRef.current?.focus(), 200);
            }
        }
    }, [open]);

    // Other components (notably SupportChat) can dispatch a custom event to
    // pop the widget open. Keeps coupling loose — no React context needed.
    useEffect(() => {
        const handler = () => {
            if (!hidden && user) setOpen(true);
        };
        window.addEventListener('qf:open-feedback', handler);
        return () => window.removeEventListener('qf:open-feedback', handler);
    }, [hidden, user]);

    if (hidden) return null;
    if (!user) return null;

    const close = () => {
        setOpen(false);
        // Reset success state after a moment so the next open is fresh.
        setTimeout(() => {
            if (submitted) {
                setSubmitted(false);
                setRating(0);
                setMessage('');
            }
        }, 400);
        localStorage.setItem(DISMISS_TIMESTAMP_KEY, String(Date.now()));
    };

    const submit = async () => {
        if (!rating) return;
        const text = message.trim();
        if (!text) return;
        setSubmitting(true);
        try {
            await api.post('/feedback', {
                rating,
                message: text,
                surface: 'popup',
                page_url: window.location.pathname,
            });
            setSubmitted(true);
            // Auto-close after 2.5s on success
            setTimeout(() => close(), 2500);
        } catch (_e) {
            // The widget swallows errors — fallback messaging keeps UX
            // friendly. The full-page form surfaces real error states.
        } finally {
            setSubmitting(false);
        }
    };

    const labelForRating = (n) => {
        if (n <= 1) return 'Not great';
        if (n === 2) return 'Could be better';
        if (n === 3) return "It's okay";
        if (n === 4) return 'Pretty good';
        return 'Love it';
    };

    const displayRating = hoverRating || rating;

    return (
        <>
            {/* Floating launcher pill — bottom-LEFT to avoid overlapping SupportChat */}
            {!open && (
                <div className="fixed bottom-4 left-4 z-40">
                    <button
                        onClick={() => setOpen(true)}
                        className="group bg-white hover:bg-slate-50 text-slate-900 rounded-full shadow-lg pl-2 pr-4 py-2 flex items-center gap-2 transition-transform hover:-translate-y-0.5 ring-1 ring-slate-200"
                        data-testid="feedback-widget-launcher"
                        title="Share feedback"
                    >
                        <span className="h-7 w-7 rounded-full bg-amber-500 flex items-center justify-center text-slate-950 shadow-inner">
                            <MessageSquareHeart className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </span>
                        <span className="text-sm font-medium">Feedback</span>
                    </button>
                </div>
            )}

            {/* Lift-up card */}
            {open && (
                <div
                    className="fixed bottom-4 left-4 z-50 w-[92vw] max-w-[380px] bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-3"
                    data-testid="feedback-widget-panel"
                >
                    {/* Header */}
                    <div className="px-4 py-3 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg overflow-hidden ring-2 ring-amber-500/40 flex items-center justify-center bg-amber-500">
                                <img src="/branding/qf-logo.jpg" alt="QuotesFlow" className="h-full w-full object-cover" draggable={false} />
                            </div>
                            <div className="leading-tight">
                                <div className="text-sm font-display font-semibold">How&apos;s QuotesFlow so far?</div>
                                <div className="text-[10px] text-slate-300">Your feedback shapes the roadmap</div>
                            </div>
                        </div>
                        <button
                            onClick={close}
                            className="text-slate-300 hover:text-white rounded p-1"
                            data-testid="feedback-widget-close"
                            aria-label="Close feedback"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="p-4 bg-white">
                        {submitted ? (
                            <div className="py-6 text-center" data-testid="feedback-widget-success">
                                <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                                    <Check className="h-6 w-6 text-emerald-600" strokeWidth={2.5} />
                                </div>
                                <div className="text-sm font-display font-semibold text-slate-900">Thanks — got it.</div>
                                <div className="text-xs text-slate-500 mt-1">We read every note. You may hear back at your account email.</div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Rating</div>
                                    <div className="flex items-center gap-1" data-testid="feedback-widget-rating">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onMouseEnter={() => setHoverRating(n)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                onClick={() => setRating(n)}
                                                aria-label={`${n} out of 5 stars`}
                                                data-testid={`feedback-widget-star-${n}`}
                                                className="p-1 rounded transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                                            >
                                                <Star
                                                    className={`h-7 w-7 ${
                                                        n <= displayRating
                                                            ? 'text-amber-500 fill-amber-500'
                                                            : 'text-slate-300'
                                                    }`}
                                                    strokeWidth={1.5}
                                                />
                                            </button>
                                        ))}
                                        {displayRating > 0 && (
                                            <span className="ml-2 text-xs font-medium text-slate-600" data-testid="feedback-widget-rating-label">
                                                {labelForRating(displayRating)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-1.5">Your thoughts</div>
                                    <Textarea
                                        ref={textareaRef}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder={
                                            rating === 0
                                                ? 'What works, what doesn\u2019t, any features you wish we had…'
                                                : rating >= 4
                                                  ? 'What are we doing right? Anything to make it even better?'
                                                  : 'What can we fix or improve? Be specific so we can act on it.'
                                        }
                                        rows={4}
                                        maxLength={2000}
                                        data-testid="feedback-widget-message"
                                        className="text-sm resize-none"
                                    />
                                </div>

                                <div className="flex items-center justify-between gap-2">
                                    <a
                                        href="/app/feedback"
                                        className="text-xs text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline"
                                        data-testid="feedback-widget-expand-link"
                                    >
                                        More detailed feedback →
                                    </a>
                                    <Button
                                        onClick={submit}
                                        disabled={submitting || !rating || !message.trim()}
                                        size="sm"
                                        className="bg-slate-900 hover:bg-slate-800 text-white"
                                        data-testid="feedback-widget-submit"
                                    >
                                        {submitting ? (
                                            <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Sending</>
                                        ) : (
                                            'Send feedback'
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default FeedbackWidget;
